<template>
  <div class="profile__item-container">
    <div
      id="mfa"
      :class="['profile__item', canEdit && 'profile__item--opened']"
      @click="toggleUpdateSection()"
    >
      <div class="profile__icon-wrapper">
        <CreditCardIcon size="1x" class="profile__icon" />
      </div>
      <div class="profile__item-data-wrapper">
        <p class="profile__item-lead">Payment</p>
        <p class="profile__item-data">{{ paymentLabel }}</p>
      </div>
      <div
        :class="[
          'dashboard__action-icon',
          'btn',
          'btn--secondary',
          'btn--training-xsmall',
          canEdit && 'dashboard__action-icon--hide',
        ]"
      >
        <Edit2Icon />
      </div>
    </div>
    <transition name="longfade">
      <div v-show="isUpdatingSectionOpened" class="profile__item-edit-wrapper">
        <div class="manage__payment-updating-container">
          <div class="manage__payment-select-container">
            <PaymentMethod
              :paymentMethod="paymentMethodSelected"
              @update-payment-method="updatePaymentMethod"
            />
          </div>

          <div class="manage__payment-method-container">
            <AddCreditCardForm
              v-if="isCreditCard"
              :isLoading="isLoading"
              :selfSubmit="false"
              @change="enableSave"
              @handle-recurly-token="handleRecurlyToken"
            />
            <PaypalSession
              v-if="isPaypal"
              @handle-recurly-token="handleRecurlyToken"
            />
            <div class="profile__item-btn-wrapper manage__btn-wrapper">
              <ActionLoader v-if="isLoading" name="ball-clip-rotate" />
              <template v-else>
                <button
                  v-if="isCreditCard"
                  type="submit"
                  form="card-form"
                  class="btn btn--primary profile__item-edit-btn"
                  :disabled="isSaveDisabled"
                >
                  Save
                </button>
                <button
                  class="btn btn--tertiary profile__item-edit-btn"
                  @click="toggleUpdateSection()"
                >
                  Cancel
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ActionLoader from '@/components/shared/ActionLoader.vue'
import AddCreditCardForm from '@/components/forms/payments/AddCreditCardForm.vue'
import { CreditCardIcon, Edit2Icon } from 'vue-feather-icons'
import PaymentMethod from '@/components/forms/payments/PaymentMethod.vue'
import PaypalSession from '@/components/forms/payments/PaypalSession.vue'

export default {
  name: 'ManagePayment',
  components: {
    CreditCardIcon,
    Edit2Icon,
    ActionLoader,
    PaymentMethod,
    PaypalSession,
    AddCreditCardForm,
  },
  props: {
    canEdit: {
      type: Boolean,
      default() {
        return false
      },
    },
    paymentInfo: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      paymentMethodSelected: '',
      paypal: '',
      isLoading: false,
      editData: false,
      isSaveDisabled: true,
    }
  },
  computed: {
    recurlyKey() {
      return process.env.VUE_APP_RECURLY_KEY
    },
    isUpdatingSectionOpened() {
      return this.canEdit && this.editData
    },
    isPaypal() {
      return this.paymentMethodSelected === 'paypal_billing_agreement'
    },
    isCreditCard() {
      return this.paymentMethodSelected === 'credit_card'
    },
    paymentLabel() {
      if (this.paymentInfo?.payment_method === 'paypal_billing_agreement')
        return 'Paypal'
      if (this.paymentInfo?.payment_method === 'credit_card')
        return this.paymentInfo?.last4_of_cc
          ? `Card ending in ${this.paymentInfo?.last4_of_cc}`
          : 'Credit card'
      return 'None'
    },
    isPaypalSigned() {
      return this.paymentInfo?.payment_method === 'paypal_billing_agreement'
    },
    paypalLabel() {
      let text = "You're signed in to your PayPal account"
      text += this.paymentInfo?.paypal_email
        ? ` as ${this.paymentInfo?.paypal_email}`
        : ''
      return text
    },
    countryCodesLabels() {
      return this.countryCodes.map(country => {
        return { label: country.name, code: country.value }
      })
    },
  },
  watch: {
    paypal: {
      handler: 'enableSave',
    },
  },
  created() {
    window.recurly.configure(this.recurlyKey)
    this.paymentMethodSelected = this.paymentInfo?.payment_method
  },
  methods: {
    toggleUpdateSection() {
      this.$emit('itemActive', 'payment')
      this.editData = !this.isUpdatingSectionOpened
      if (this.isUpdatingSectionOpened) this.$emit('itemActive', '')
    },
    updatePaymentMethod(method) {
      this.paymentMethodSelected = method
    },
    async submitToken(recurlyTokenId) {
      if (recurlyTokenId) {
        await this.$store.dispatch(
          'subscriptions/updatePaymentMethod',
          recurlyTokenId
        )
      } else {
        this.$store.dispatch(
          'app/setError',
          'Unable to update your payment method with the provided entries. Please try another payment method'
        )
      }
    },
    async handleRecurlyToken(token) {
      const recurlyTokenId = token.id
      try {
        this.isLoading = true
        await this.submitToken(recurlyTokenId)
        await this.$store.dispatch('subscriptions/getPaymentInfo')
        this.toggleUpdateSection()
      } catch (error) {
        this.$store.dispatch('app/setError', error)
        return error
      } finally {
        this.isLoading = false
      }
    },
    handlePaypalErrors(error) {
      if (error?.message) {
        return this.$store.dispatch('app/setError', error.message)
      }
      return this.$store.dispatch(
        'app/setError',
        'Something went wrong with PayPal. Please try again.'
      )
    },
    createPaypal() {
      this.paypal = window.recurly.PayPal({
        display: { displayName: ' INE ' },
      })
      this.paypal.on('error', this.handlePaypalErrors)
      this.paypal.on('token', this.handleRecurlyToken)
      this.paypal.start()
    },
    enableSave() {
      if (this.isSaveDisabled) this.isSaveDisabled = false
    },
  },
}
</script>

<style lang="scss">
.manage {
  display: flex;
  align-items: center;
  justify-content: center;

  &__payment {
    &-updating-container {
      display: flex;
    }
    &-method-container {
      width: 100%;
    }
  }

  &__btn-wrapper {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
